import { forwardRef, useRef, useState } from "react";
import { type DateValue, useButton, useDatePicker } from "react-aria";
import { type DatePickerStateOptions, useDatePickerState } from "react-stately";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useForwardedRef } from "@/lib/useForwardedRef";
import { cn } from "@/lib/utils";
import sprites from "/src/shared/assets/sprite.svg";
import { Calendar } from "./Calendar";
import { DateField } from "./DateField";

const DatePicker = forwardRef<
	HTMLDivElement,
	DatePickerStateOptions<DateValue>
>((props, forwardedRef) => {
	const ref = useForwardedRef(forwardedRef);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);

	const [open, setOpen] = useState(false);

	const state = useDatePickerState(props);
	const {
		groupProps,
		fieldProps,
		buttonProps: _buttonProps,
		dialogProps,
		calendarProps,
	} = useDatePicker(props, state, ref);
	const { buttonProps } = useButton(_buttonProps, buttonRef);

	return (
		<div
			{...groupProps}
			ref={ref}
			className={cn(
				groupProps.className,
				`flex items-center bg-background h-[18px] text-xs cursor-pointer 
					text-accent-foreground placeholder:text-accent-foreground/60 
					border border-gray hover:border-light-gray/50 
					focus:border-light-gray/70 outline-hidden rounded-md 
					transition-all disabled:cursor-not-allowed disabled:opacity-50`,
			)}
		>
			<DateField aria-label="date-field" {...fieldProps} />
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						{...buttonProps}
						variant="outline"
						className="h-[18px] w-6 px-0 rounded-l-none border-r-0"
						disabled={props.isDisabled}
						onClick={() => setOpen(true)}
					>
						<svg
							aria-label="calendar"
							role="img"
							fill="#ffffffcc"
							className="w-3 h-3"
						>
							<use xlinkHref={`${sprites}#calendar`} />
						</svg>
					</Button>
				</PopoverTrigger>
				<PopoverContent ref={contentRef} className="w-full p-2">
					<div {...dialogProps} className="space-y-3">
						<Calendar {...calendarProps} />
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
});

DatePicker.displayName = "DatePicker";

export { DatePicker };
